import * as React from 'react';

import { Typography } from '@mui/material';
import { CenterCircularProgress, EmptyBox, useModal } from '@europrocurement/flexy-components';
import { associateInvoiceToReminderPublisherIcon } from '@europrocurement/l2d-icons';
import {
    AchatsAppDispatch,
    FactureAchat,
    FactureAchatApiObject,
    ReminderPublisher,
    useAchatsService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { useApiRequestControllable } from '@europrocurement/l2d-modules';
import { DataSource, getDataThunkType } from '@europrocurement/l2d-redux-utils';
import {
    ReminderPublisherApiAddFacturesReminderPublisherItemRequest,
    ReminderPublisherApiSuggestReminderPublishersReminderPublisherCollectionRequest,
    SuggestReminderPublishersReminderPublisherCollection200Response,
    SuggestReminderPublishersReminderPublisherCollection200ResponseSuggestionsInner,
} from '@europrocurement/l2d-domain/openApi/ApiAchats';
import { useDispatch } from 'react-redux';

import FormModal from '../../../../../../../Achats/components/modals/FormModal/FormModal';
import { ChoiceProps } from '../../../../../../../Achats/components/modals/FormModal/types';

/**
 * Type pour une facture dans les suggestions du serveur
 */
type InvoiceType =
    SuggestReminderPublishersReminderPublisherCollection200ResponseSuggestionsInner['factures'][0];

/**
 * Modal affichée pendant le chargement des factures
 */
const LoadingInvoicesModal: React.FunctionComponent = function () {
    return (
        <div
            style={{
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
            }}
        >
            <CenterCircularProgress />
            <Typography variant="body1">Chargement des factures disponibles...</Typography>
        </div>
    );
};

/**
 * Custom hook pour gérer la modal d'association de factures à un rappel fournisseur
 */
export function useAssociateInvoiceModal(
    reminderPublisher?: ReminderPublisher,
    dataSourceInvoices?: DataSource<FactureAchat>,
    fetchInvoices?: getDataThunkType<FactureAchatApiObject>,
) {
    const { reminderPublisherApi, selectReminderPublisher } = useAchatsService();

    const xIdSociete = useSociete();
    const dispatch = useDispatch<AchatsAppDispatch>();
    const { modalActions } = useModal();
    const { request: suggestionRequest } = useApiRequestControllable();
    const { request: associateRequest } = useApiRequestControllable();

    // État local pour le traitement
    const [isProcessing, setIsProcessing] = React.useState(false);

    // Référence pour les factures sélectionnées
    const selectedInvoicesRef = React.useRef<InvoiceType[]>([]);

    // Référence pour éviter les soumissions multiples
    const isSubmittingRef = React.useRef(false);

    // Références pour les fonctions qui dépendent l'une de l'autre
    const associateInvoicesRef = React.useRef<() => Promise<void>>();
    const loadAndShowInvoiceSelectionModalRef = React.useRef<() => Promise<void>>();

    /**
     * Vérifie si une facture est déjà associée au rappel fournisseur
     */
    const isInvoiceAlreadyAssociated = React.useCallback(
        (invoice: InvoiceType, dataSource: DataSource<FactureAchat>): boolean =>
            !!dataSource.data?.find(
                (associatedInvoice) => associatedInvoice?.id?.toString() === invoice.id.toString(),
            ),
        [],
    );

    /**
     * Gère la fermeture de la modal
     */
    const handleCloseModal = React.useCallback(() => {
        // Réinitialisation des états
        selectedInvoicesRef.current = [];
        isSubmittingRef.current = false;
        setIsProcessing(false);
        modalActions.close();
    }, [modalActions]);

    // Initialiser les fonctions dans un useEffect pour éviter les dépendances circulaires
    React.useEffect(() => {
        // Fonction pour effectuer l'association des factures
        associateInvoicesRef.current = async () => {
            // Si déjà en cours de soumission ou pas de données valides, ne rien faire
            if (
                isSubmittingRef.current ||
                !reminderPublisher?.id ||
                selectedInvoicesRef.current.length === 0
            ) {
                return;
            }

            // Marquer comme en cours de soumission
            isSubmittingRef.current = true;
            setIsProcessing(true);

            // Remplacer la modal actuelle par une modal de chargement
            modalActions.close();
            modalActions.call(
                <div
                    style={{
                        padding: 20,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 12,
                    }}
                >
                    <CenterCircularProgress />
                    <Typography variant="body1">Association en cours...</Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Veuillez patienter pendant le traitement.
                    </Typography>
                </div>,
            );

            try {
                const requestParameters: ReminderPublisherApiAddFacturesReminderPublisherItemRequest =
                    {
                        id: reminderPublisher.id,
                        addFacturesReminderPublisherItemRequest: {
                            factureIds: selectedInvoicesRef.current.map((invoice) =>
                                invoice.id.toString(),
                            ),
                        },
                    };

                await associateRequest(
                    reminderPublisherApi.addFacturesReminderPublisherItem(requestParameters),
                    {
                        withToaster: true,
                        successMessage: 'Association de la facture avec succès.',
                        successCallback: () => {
                            if (fetchInvoices) {
                                setTimeout(() => {
                                    dispatch(fetchInvoices({}));
                                    if (reminderPublisher?.id) {
                                        dispatch(
                                            selectReminderPublisher({ id: reminderPublisher.id }),
                                        );
                                    }
                                }, 1500);
                            }
                            handleCloseModal();
                        },
                        failureCallback: () => {
                            // En cas d'erreur, réinitialiser l'état et réafficher la modal de sélection
                            isSubmittingRef.current = false;
                            setIsProcessing(false);

                            // Réafficher la modal de sélection
                            modalActions.close();
                            if (loadAndShowInvoiceSelectionModalRef.current) {
                                loadAndShowInvoiceSelectionModalRef.current();
                            }
                        },
                    },
                );
            } catch (error) {
                // En cas d'erreur non gérée
                isSubmittingRef.current = false;
                setIsProcessing(false);
                modalActions.close();
                console.error(error);
                modalActions.call(
                    <EmptyBox
                        text="Erreur lors de l'association"
                        subText="Une erreur inattendue s'est produite. Veuillez réessayer."
                    />,
                );
            }
        };

        // Fonction pour charger et afficher la modal de sélection
        loadAndShowInvoiceSelectionModalRef.current = async () => {
            if (!reminderPublisher || !dataSourceInvoices) return;

            try {
                // Paramètres de la requête
                const requestParameters: ReminderPublisherApiSuggestReminderPublishersReminderPublisherCollectionRequest =
                    {
                        fournisseurId: reminderPublisher.fournisseurId,
                        idSociete: xIdSociete,
                    };

                // Effectuer la requête pour obtenir les suggestions
                const result = await suggestionRequest(
                    reminderPublisherApi.suggestReminderPublishersReminderPublisherCollection(
                        requestParameters,
                    ),
                    { withToaster: false },
                );

                const { suggestions } =
                    result.data as SuggestReminderPublishersReminderPublisherCollection200Response;

                const foundSuggestion = suggestions.find(
                    (suggestion) =>
                        suggestion.fournisseur.id.toString() ===
                        reminderPublisher.fournisseurId.toString(),
                );

                if (!foundSuggestion) {
                    modalActions.call(
                        <EmptyBox text="Aucune suggestion trouvée pour ce fournisseur" />,
                    );
                    return;
                }

                // Filtrer les factures qui ne sont pas déjà associées
                const availableInvoices = foundSuggestion.factures.filter(
                    (invoice) => !isInvoiceAlreadyAssociated(invoice, dataSourceInvoices),
                );

                if (availableInvoices.length === 0) {
                    modalActions.call(<EmptyBox text="Pas de factures disponibles" />);
                    return;
                }

                // Construire les choix pour le formulaire
                const choices: ChoiceProps[] = availableInvoices.map((invoice) => ({
                    label: `${invoice.id} - ${invoice.numeroFacture}`,
                    value: invoice.id.toString(),
                    action: (_, checked) => {
                        if (checked) {
                            // Ajouter la facture à la sélection si elle n'y est pas déjà
                            const isAlreadySelected = selectedInvoicesRef.current.some(
                                (item) => item.id === invoice.id,
                            );
                            if (!isAlreadySelected) {
                                selectedInvoicesRef.current = [
                                    ...selectedInvoicesRef.current,
                                    invoice,
                                ];
                            }
                        } else {
                            // Retirer la facture de la sélection
                            selectedInvoicesRef.current = selectedInvoicesRef.current.filter(
                                (item) => item.id !== invoice.id,
                            );
                        }
                    },
                    defaultSelected: false,
                }));

                // Si la première option est disponible, la sélectionner par défaut
                if (choices.length > 0) {
                    choices[0].defaultSelected = true;
                    // Ajouter la première facture à la sélection par défaut
                    selectedInvoicesRef.current = [availableInvoices[0]];
                }

                // Créer une fonction sécurisée pour l'association
                const safeAssociateInvoices = () => {
                    if (!isSubmittingRef.current && associateInvoicesRef.current) {
                        associateInvoicesRef.current();
                    }
                };

                // Afficher la modal avec les choix
                modalActions.call(
                    <FormModal
                        title="Associer une facture"
                        context="Sélectionner une facture à ajouter au rappel fournisseur."
                        inputNature="select"
                        choices={choices}
                        icon={associateInvoiceToReminderPublisherIcon}
                        submitActions={[
                            {
                                type: 'cancel',
                                action: handleCloseModal,
                                disabled: isProcessing,
                            },
                            {
                                type: 'confirm',
                                action: safeAssociateInvoices,
                                disabled: isProcessing,
                                startIcon: isProcessing ? <CenterCircularProgress /> : undefined,
                                label: isProcessing ? 'Association en cours...' : 'Confirmer',
                            },
                        ]}
                    />,
                );
            } catch (error) {
                console.error('Erreur lors du chargement des suggestions:', error);
                modalActions.call(
                    <EmptyBox
                        text="Erreur lors du chargement des factures"
                        subText={(error as Error).message}
                    />,
                );
            }
        };
    }, [
        associateRequest,
        dispatch,
        dataSourceInvoices,
        fetchInvoices,
        handleCloseModal,
        isInvoiceAlreadyAssociated,
        isProcessing,
        modalActions,
        reminderPublisher,
        suggestionRequest,
        xIdSociete,
        reminderPublisherApi,
        selectReminderPublisher,
    ]);

    /**
     * Ouvre la modal d'association de factures
     */
    const openAssociateModal = React.useCallback(() => {
        if (!reminderPublisher) return;

        // Réinitialiser les états
        selectedInvoicesRef.current = [];
        isSubmittingRef.current = false;
        setIsProcessing(false);

        // Afficher d'abord la modal avec l'indicateur de chargement
        modalActions.call(<LoadingInvoicesModal />);

        // Charger les factures et afficher la modal de sélection
        if (loadAndShowInvoiceSelectionModalRef.current) {
            loadAndShowInvoiceSelectionModalRef.current().catch((error) => {
                console.error('Erreur non gérée:', error);
                modalActions.call(
                    <EmptyBox
                        text="Une erreur inattendue s'est produite"
                        subText="Veuillez réessayer ultérieurement."
                    />,
                );
            });
        }
    }, [modalActions, reminderPublisher]);

    return {
        openAssociateModal,
        isProcessing,
    };
}
